import {
  useEffect, useRef, useState, useCallback, createContext, useContext, useMemo, useId,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Check, AlertTriangle, Info, XCircle } from 'lucide-react';
import cn from '../../lib/cn';
import { Button, Textarea, Input, Field, Alert } from './primitives';

/**
 * Overlay layer — modal, drawer, tooltip, toast.
 *
 * This is where hand-building instead of using a component library actually
 * costs something, so the behaviour a library would have given us is written
 * out explicitly: focus is trapped and restored, Escape closes, the background
 * is inert to screen readers, and body scroll is locked while an overlay is up.
 */

// ── Shared behaviour ────────────────────────────────────────────────────────

/** Lock body scroll, compensating for the scrollbar so the page does not jump. */
function useScrollLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousPadding = body.style.paddingRight;
    const gap = window.innerWidth - document.documentElement.clientWidth;

    body.style.overflow = 'hidden';
    if (gap > 0) body.style.paddingRight = `${gap}px`;

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPadding;
    };
  }, [active]);
}

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])',
  'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Trap Tab inside the container while it is open, and return focus to whatever
 * was focused before on close. Without this, tabbing out of a dialog lands on
 * the page behind it — which for a confirmation dialog means the operator can
 * reach the very button they were being asked to confirm.
 */
function useFocusTrap(active, containerRef) {
  useEffect(() => {
    if (!active) return undefined;

    const previouslyFocused = document.activeElement;
    const container = containerRef.current;

    const focusFirst = () => {
      if (!container) return;
      const targets = container.querySelectorAll(FOCUSABLE);
      // Prefer the first field over the first button, so a form dialog opens
      // ready to type rather than ready to submit.
      const preferred = container.querySelector('input, textarea, select') || targets[0];
      preferred?.focus?.();
    };

    const raf = requestAnimationFrame(focusFirst);

    const onKeyDown = (event) => {
      if (event.key !== 'Tab' || !container) return;
      const targets = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!targets.length) return;

      const first = targets[0];
      const last = targets[targets.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [active, containerRef]);
}

function useEscape(active, onEscape) {
  useEffect(() => {
    if (!active) return undefined;
    const handler = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onEscape?.();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [active, onEscape]);
}

// ── Modal ───────────────────────────────────────────────────────────────────

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Modal({ open, onClose, title, description, children, footer, size = 'md' }) {
  const panelRef = useRef(null);
  const titleId = useId();

  useScrollLock(open);
  useFocusTrap(open, panelRef);
  useEscape(open, onClose);

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-6">
          <motion.div
            className="fixed inset-0 bg-black/25"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16 }}
            onClick={onClose}
          />

          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className={cn(
              'relative z-10 my-auto w-full overflow-hidden rounded-sheet bg-surface shadow-sheet',
              SIZES[size],
            )}
            // Apple's sheet motion: a short spring that settles, not a fade.
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 460, damping: 38, mass: 0.9 }}
          >
            <div className="flex items-start justify-between gap-4 hairline-b px-5 py-4">
              <div className="min-w-0">
                <h2 id={titleId} className="text-body font-semibold text-label">
                  {title}
                </h2>
                {description && (
                  <p className="mt-1 text-footnote leading-relaxed text-label3">{description}</p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="-mr-1 shrink-0 rounded p-1 text-label3 transition-colors hover:bg-fill/10 hover:text-label"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-5 py-4">{children}</div>

            {footer && (
              <div className="flex items-center justify-end gap-2 hairline-t px-5 py-3.5">
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

// ── Drawer ──────────────────────────────────────────────────────────────────

/** Right-hand slide-over for record detail. Same trapping rules as Modal. */
export function Drawer({ open, onClose, title, subtitle, children, footer, width = 'max-w-xl' }) {
  const panelRef = useRef(null);
  const titleId = useId();

  useScrollLock(open);
  useFocusTrap(open, panelRef);
  useEscape(open, onClose);

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <motion.div
            className="absolute inset-0 bg-black/25"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16 }}
            onClick={onClose}
          />

          <motion.aside
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className={cn(
              'material-thick hairline-l relative z-10 flex h-full w-full flex-col shadow-sheet',
              width,
            )}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 340, damping: 36 }}
          >
            <div className="flex items-start justify-between gap-4 hairline-b px-5 py-4">
              <div className="min-w-0">
                <h2 id={titleId} className="truncate text-body font-semibold text-label">
                  {title}
                </h2>
                {subtitle && <p className="mt-0.5 truncate text-footnote text-label3">{subtitle}</p>}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="-mr-1 shrink-0 rounded p-1 text-label3 transition-colors hover:bg-fill/10 hover:text-label"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

            {footer && (
              <div className="hairline-t px-5 py-3.5">{footer}</div>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

// ── Confirm ─────────────────────────────────────────────────────────────────

/**
 * Destructive-action gate.
 *
 * With `confirmPhrase` the button stays disabled until the operator types the
 * phrase back exactly (§13.5.8). The main platform reaches an identical
 * cascading purge by accident — when the last member of an org deletes their
 * own account — so here it takes deliberate effort.
 */
export function ConfirmDialog({
  open, onClose, onConfirm, title, description, children, confirmPhrase,
  confirmLabel = 'Confirm', tone = 'primary', loading, withReason = true,
}) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) {
      setTyped('');
      setReason('');
    }
  }, [open]);

  const canConfirm = !confirmPhrase || typed.trim() === confirmPhrase;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            loading={loading}
            disabled={!canConfirm || loading}
            onClick={() => onConfirm({ reason, confirmName: typed.trim() })}
          >
            {confirmLabel}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        {children}

        {withReason && (
          <Field label="Reason" hint="Recorded in the audit log alongside this action.">
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Why are you doing this?"
            />
          </Field>
        )}

        {confirmPhrase && (
          <div className="space-y-3">
            <Alert tone="red" icon={<AlertTriangle className="h-4 w-4" />}>
              This cannot be undone.
            </Alert>
            <Field
              label={`Type “${confirmPhrase}” to confirm`}
              error={typed && !canConfirm ? 'Does not match yet.' : undefined}
            >
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="font-mono"
              />
            </Field>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Tooltip ─────────────────────────────────────────────────────────────────

/**
 * Hover/focus tooltip.
 *
 * Rendered in a portal with fixed positioning measured from the trigger, so it
 * escapes the `overflow: hidden` on scrolling table containers — the usual
 * reason a tooltip gets clipped in a data grid.
 */
export function Tooltip({ content, children, side = 'top', className }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const offsets = {
      top: { top: r.top - 8, left: r.left + r.width / 2 },
      bottom: { top: r.bottom + 8, left: r.left + r.width / 2 },
      left: { top: r.top + r.height / 2, left: r.left - 8 },
      right: { top: r.top + r.height / 2, left: r.right + 8 },
    };
    setCoords(offsets[side]);
  }, [side]);

  const show = () => { place(); setOpen(true); };
  const hide = () => setOpen(false);

  if (!content) return children;

  const translate = {
    top: 'translate(-50%, -100%)',
    bottom: 'translate(-50%, 0)',
    left: 'translate(-100%, -50%)',
    right: 'translate(0, -50%)',
  }[side];

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className={cn('inline-flex', className)}
      >
        {children}
      </span>
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              role="tooltip"
              className="material-thick pointer-events-none fixed z-[60] max-w-xs rounded-field px-2.5 py-1.5 text-caption leading-relaxed text-label shadow-popover ring-1 ring-inset ring-separator"
              style={{ top: coords.top, left: coords.left, transform: translate }}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.12 }}
            >
              {content}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}

// ── Toast ───────────────────────────────────────────────────────────────────

const ToastContext = createContext(null);

const TOAST_ICONS = {
  success: <Check className="h-4 w-4 text-green-400" />,
  error: <XCircle className="h-4 w-4 text-red-400" />,
  warning: <AlertTriangle className="h-4 w-4 text-orange-400" />,
  info: <Info className="h-4 w-4 text-teal-400" />,
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const counter = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((message, { tone = 'info', duration = 5200, title } = {}) => {
    counter.current += 1;
    const id = counter.current;
    setToasts((current) => [...current, { id, message, tone, title }]);
    // Errors linger: they usually carry something the operator needs to read
    // and act on, rather than a confirmation they already expected.
    const ttl = tone === 'error' ? Math.max(duration, 9000) : duration;
    setTimeout(() => dismiss(id), ttl);
    return id;
  }, [dismiss]);

  const api = useMemo(() => ({
    push,
    dismiss,
    success: (m, o) => push(m, { ...o, tone: 'success' }),
    error: (m, o) => push(m, { ...o, tone: 'error' }),
    warning: (m, o) => push(m, { ...o, tone: 'warning' }),
    info: (m, o) => push(m, { ...o, tone: 'info' }),
  }), [push, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          className="pointer-events-none fixed bottom-4 right-4 z-[70] flex w-full max-w-sm flex-col gap-2"
          role="region"
          aria-live="polite"
          aria-label="Notifications"
        >
          <AnimatePresence initial={false}>
            {toasts.map((toast) => (
              <motion.div
                key={toast.id}
                layout
                initial={{ opacity: 0, y: 16, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 460, damping: 38 }}
                className="material-thick pointer-events-auto flex items-start gap-3 rounded-card px-3.5 py-3 shadow-popover ring-1 ring-inset ring-separator"
              >
                <span className="mt-0.5 shrink-0">{TOAST_ICONS[toast.tone]}</span>
                <div className="min-w-0 flex-1">
                  {toast.title && (
                    <p className="text-footnote font-semibold text-label">{toast.title}</p>
                  )}
                  <p className={cn('text-footnote leading-relaxed text-label2', toast.title && 'mt-0.5')}>
                    {toast.message}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(toast.id)}
                  aria-label="Dismiss"
                  className="-mr-1 -mt-0.5 shrink-0 rounded p-1 text-label4 transition-colors hover:bg-fill/10 hover:text-label2"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside <ToastProvider>');
  return context;
}
